import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet } from "react-router-dom";
import { api } from "@/lib/api";
import { LayoutDashboard, LogOut, Mic2, Radio, Settings, Users, ExternalLink, Inbox, Home, PanelTop, FileText, UploadCloud, Radar, ClipboardList, Globe2, FileBarChart2, AudioLines, Telescope, Sparkles, MessageSquareQuote, MessagesSquare, Newspaper } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/homepage", label: "Homepage", icon: Home, id: "homepage" },
  { to: "/admin/menus", label: "Menus", icon: PanelTop, id: "menus" },
  { to: "/admin/pages", label: "Pages", icon: FileText, id: "pages" },
  { to: "/admin/shows", label: "Shows", icon: Radio, id: "shows" },
  { to: "/admin/episodes", label: "Episodes", icon: Mic2, id: "episodes" },
  { to: "/admin/team", label: "Team", icon: Users, id: "team" },
  { to: "/admin/publishing", label: "Publishing", icon: UploadCloud, id: "publishing" },
  { to: "/admin/club-map", label: "Club Map", icon: Globe2, id: "club-map" },
  { to: "/admin/timecodes", label: "Timecodes", icon: Radar, id: "timecodes" },
  { to: "/admin/wordfinder", label: "Keyword Finder", icon: AudioLines, id: "wordfinder" },
  { to: "/admin/podcast-finder", label: "Podcast Finder", icon: Telescope, id: "podcast-finder" },
  { to: "/admin/cleaner", label: "Podcast Cleaner", icon: Sparkles, id: "cleaner" },
  { to: "/admin/interview", label: "Interview Assistant", icon: MessagesSquare, id: "interview" },
  { to: "/admin/articles", label: "SEO Articles", icon: Newspaper, id: "articles" },
  { to: "/admin/survey", label: "Survey", icon: ClipboardList, id: "survey" },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote, id: "testimonials" },
  { to: "/admin/applications", label: "Inbox", icon: Inbox, id: "applications" },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [drafts, setDrafts] = useState(0);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const load = () => api.get("/admin/articles/counts").then((r) => setDrafts(r.data.drafts)).catch(() => {});
    load();
    const i = setInterval(load, 120000);
    window.addEventListener("articles-changed", load);
    return () => { clearInterval(i); window.removeEventListener("articles-changed", load); };
  }, [user]);
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "robots"; document.head.appendChild(meta); }
    meta.content = "noindex, nofollow, noarchive";
    return () => { meta.content = "index, follow"; };
  }, []);
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/publish" replace />;

  return (
    <div className="min-h-screen md:grid md:grid-cols-[240px_1fr]" data-testid="admin-layout">
      <aside className="border-b md:border-b-0 md:border-r border-white/10 bg-[#050B17] p-5 flex md:flex-col gap-6 md:sticky md:top-0 md:h-screen">
        <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link"><Logo size={36} /><span className="font-display font-bold hidden md:block">Underdog CMS</span></Link>
        <nav className="flex md:flex-col gap-1 flex-1 overflow-x-auto">
          {[...links, ...(user.reports_owner ? [{ to: "/admin/reports", label: "Reporting", icon: FileBarChart2, id: "reports" }] : [])].sort((a, b) => a.label.localeCompare(b.label)).map(({ to, label, icon: Icon, end, id }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${isActive ? "bg-[#053671] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`} data-testid={`admin-nav-${id}`}>
              <Icon size={18} /> <span className="hidden md:inline">{label}</span>
              {id === "articles" && drafts > 0 && <span className="ml-auto rounded-full bg-sky-400 text-[#070F1E] px-1.5 text-[0.65rem] font-bold" data-testid="admin-nav-articles-badge">{drafts}</span>}
            </NavLink>
          ))}
        </nav>
        {user?.team_id && (
          <NavLink to="/publish" className="mt-1 flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold whitespace-nowrap text-white bg-[#4CBB17] hover:bg-[#43a614] shadow-[0_4px_14px_rgba(76,187,23,0.35)] transition-colors" data-testid="admin-nav-publish-as">
            <Mic2 size={16} /> <span className="hidden md:inline">Publish as {user.name.split(" ")[0]}</span>
          </NavLink>
        )}
        <div className="md:space-y-2 flex md:flex-col gap-2">
          <Link to="/" className="flex items-center gap-2 text-xs text-slate-400 hover:text-white px-3" data-testid="admin-view-site-link"><ExternalLink size={14} /> <span className="hidden md:inline">View site</span></Link>
          <button onClick={logout} className="flex items-center gap-2 text-xs text-slate-400 hover:text-rose-400 px-3" data-testid="admin-logout-button"><LogOut size={14} /> <span className="hidden md:inline">Sign out</span></button>
        </div>
      </aside>
      <main className="p-5 sm:p-8 lg:p-10 max-w-6xl w-full"><Outlet /></main>
    </div>
  );
}
